import React, { Component } from 'react';
import Dropdown from 'react-bootstrap/Dropdown';
import { i18n } from '../../i18n/translate';
import { Icon, Layout } from '../../core';
import { Button } from '../Button';
import { LanguageModal } from '../Modals/LanguageModal';
import { SchoolModal } from '../Modals/SchoolModal';
import { FAIcon } from '../fa-icon';
import { HelpModal } from '../Modals/HelpModal';
import { ConfirmationModal } from '../ConfirmationModal';
import { WindowHelperWrapper } from '../../utils/WindowHelper';
import { getActiveRuleSetFromUrl } from '../../utils/UrlUtil';
import { formatClassName } from '../../utils';
import { HeaderMenuAppSwitcher } from '../HeaderMenuAppSwitcher';
import { propTypes, defaultProps } from './types';
const EXPANDED_HEADER_BREAKPOINT = 768;

/**
  This component is used to generate a Header menu from an object array.
  - It supports two types of menu items: Links and Dropdowns
  - Format for menuData:
    {
      submenu_items: [{
        icon: 'user',
        title: 'Menu Title',
        target_url: '/users',
        css_class: '',
        slug: '',
        submenu_items: [{
          icon: 'user',
          title: 'Submenu title',
          css_class: '',
          target_url: '/users/add',
          target_method: 'GET',
          new_window: false,
          slug: ''
        }]
      }]
    }
*/

export class HeaderMenu extends Component {
  constructor(props) {
    super(props);
    const serverSide = typeof window === 'undefined';
    const dropdowns = {};
    this.windowHelper = WindowHelperWrapper(props.windowOverride);
    props.menuData.submenu_items.forEach((item, key) => {
      if (item.submenu_items && item.submenu_items.length) dropdowns[`headerMenu-${key}`] = false;
    });

    this.state = {
      showConfirmationModal: false,
      showLanguageModal: false,
      showSchoolModal: false,
      showHelpModal: false,
      isMobile: serverSide && props.isMobile,
      serverSide,
      dropdowns,
      redirectRuleSetName: null,
      redirectUrl: null,
    };
  }

  componentDidMount() {
    if (!this.state.serverSide) {
      this.destroyWindowWatcher = this.windowHelper.watchBreakpoints(EXPANDED_HEADER_BREAKPOINT, this.checkHeader);
    }
  }

  componentWillUnmount() {
    if (!this.state.serverSide) {
      this.destroyWindowWatcher();
    }
  }

  checkHeader = fullWidth => {
    this.setState({ isMobile: !fullWidth });
  };

  showMenuItem(item) {
    return item.slug !== 'logout' || !this.props.isImpersonating;
  }

  getMenuLabel(item) {
    const prefix = item.css_class === 'account' && this.props.isImpersonating ? i18n.t('Impersonating ') : '';
    return `${prefix}${item.title}`;
  }

  toggleLanguageModal = () => {
    this.setState({ showLanguageModal: !this.state.showLanguageModal });
  };

  toggleSchoolModal = () => {
    this.setState({ showSchoolModal: !this.state.showSchoolModal });
  };

  toggleHelpModal = () => {
    this.setState({ showHelpModal: !this.state.showHelpModal });
  };

  toggleConfirmationModal = () => {
    if (this.props.selectedLanguage !== 'en') {
      window.location.reload();
    } else {
      this.goBackToAdminPortal();
    }
  };

  goBackToAdminPortal = () => {
    const { adminifiDomain, organizationSlug } = this.props;
    const { redirectUrl, redirectRuleSetName } = this.state;

    let url = redirectUrl;
    if (adminifiDomain) {
      url = `${adminifiDomain}/en/${organizationSlug}/cportal/reports`;
      if (redirectRuleSetName) url = `${url}?current_rule_set_name=${redirectRuleSetName}`;
    }

    this.windowHelper.redirect(url);
  };

  onToggleDropdown = (open, id, event) => {
    if (event?.source === 'rootClose') {
      this.setState({ dropdowns: {} });
    } else {
      this.toggleDropdown(id, open);
    }
  };

  toggleDropdown = (key, open) => {
    this.setState({ dropdowns: { [key]: open } });
  };

  buttonOrLink = (item, target_url, isDropdown = false) => {
    const buttonClick = {};
    if (target_url === 'language_modal') {
      buttonClick.onClick = () => this.toggleLanguageModal();
    } else if (target_url === 'school_modal') {
      buttonClick.onClick = () => this.toggleSchoolModal();
    } else if (target_url === 'help_modal') {
      buttonClick.onClick = () => this.toggleHelpModal();
    } else if (target_url === 'confirmation_modal') {
      buttonClick.onClick = () => this.toggleConfirmationModal();
    } else if (item.slug === 'logout') {
      buttonClick.onClick = () => this.props.onStopImpersonation();
      buttonClick.href = target_url;
    } else {
      buttonClick.href = target_url;
      buttonClick.target = item.new_window ? '_blank' : '_self';
      if (isDropdown) {
        buttonClick['data-method'] = item.target_method && !this.props.isImpersonating ? item.target_method : 'GET';
      }
    }
    return buttonClick;
  };

  renderLanguageModal = () => {
    return (
      <LanguageModal
        renderAsLegacy={!this.props.isHomeroom}
        appendLanguage={!!this.props?.appendLanguage}
        showLanguageModal={this.state.showLanguageModal}
        toggleLanguageModal={this.toggleLanguageModal}
        languages={this.props.languages}
        selectedLanguage={this.props.selectedLanguage}
      />
    );
  };

  renderSchoolModal = () => {
    const { homeroomDomain, user } = this.props;
    return (
      <SchoolModal
        homeroomDomain={homeroomDomain}
        showSchoolModal={this.state.showSchoolModal}
        toggleSchoolModal={this.toggleSchoolModal}
        user={user}
      />
    );
  };

  renderHelpModal = () => {
    return (
      <HelpModal
        helpMessage={this.props.helpMessage}
        helpMessageEnabled={this.props.helpMessageEnabled}
        isFoundry={this.props.isFoundry}
        showHelpModal={this.state.showHelpModal}
        toggleHelpModal={this.toggleHelpModal}
        user={this.props.user}
      />
    );
  };

  renderConfirmationModal = () => {
    return (
      <ConfirmationModal
        onConfirm={this.goBackToAdminPortal}
        onCancel={this.toggleConfirmationModal}
        show={this.state.showConfirmationModal}
      >
        {i18n.t(
          'You have selected to switch to the Admin Dashboard. The Admin Dashboard currently supports English only.'
        )}
      </ConfirmationModal>
    );
  };

  runSwitch = redirectUrl => {
    const url = new URL(redirectUrl);
    const current_rule_set_name = url.searchParams.get('current_rule_set_name');
    if (current_rule_set_name === 'cc_admin' && this.props.selectedLanguage !== 'en') {
      this.setState({ showConfirmationModal: true, redirectRuleSetName: current_rule_set_name, redirectUrl });
    } else {
      this.windowHelper.redirect(redirectUrl);
    }
  };

  renderDropdown = (menuItem, key) => {
    const { currentRuleSets, showCloseButton } = this.props;
    // handle special app-switcher menu
    if (menuItem.slug === 'app-switcher') {
      const hasActiveRuleSet = getActiveRuleSetFromUrl(currentRuleSets);
      // show app switcher if there is an active rule set
      if (hasActiveRuleSet?.name) {
        return (
          <HeaderMenuAppSwitcher
            currentRuleSets={currentRuleSets}
            menuItems={menuItem.submenu_items}
            onRedirect={this.runSwitch}
          />
        );
      } else {
        return null;
      }
    }

    const isAccount = menuItem.css_class === 'account';
    const menuId = `headerMenu-${key}`;
    const menuLabel = this.getMenuLabel(menuItem);

    return (
      <Dropdown
        id={menuId}
        className={menuItem.css_class}
        onToggle={(open, event) => this.onToggleDropdown(open, menuId, event)}
        show={this.state.dropdowns[menuId]}
      >
        <Dropdown.Toggle
          as={Button}
          rounded="full"
          variant="solid"
          colorScheme="neutral"
          size="compact"
          aria-label={menuLabel}
          {...(!isAccount &&
            menuItem.icon && {
              leftIcon: <Icon additionalClasses="left" iconName={menuItem.icon} />,
            })}
          {...(isAccount && {
            rightIcon: 'bars',
          })}
        >
          <span className="btn-text" aria-hidden={true}>
            {menuLabel}
          </span>
        </Dropdown.Toggle>
        {/* style: left is temporary until figuring out why Dropdown is not positioned correctly */}
        <Dropdown.Menu className="user-menu" style={{ left: '100%', transform: 'translateX(-100%)' }}>
          {showCloseButton && (
            <div
              className="mobile-close"
              aria-label={i18n.t('Close')}
              onClick={() => this.toggleDropdown(menuId, false)}
            >
              &times;
            </div>
          )}
          {this.renderDropdownMenuItems(menuItem)}
        </Dropdown.Menu>
      </Dropdown>
    );
  };

  renderDropdownMenuItems = (menuItem, menuItemElements = []) => {
    menuItem.submenu_items.forEach((item, subKey) => {
      if (item.subsection) {
        if (subKey) {
          menuItemElements.push(<Dropdown.Divider key={menuItemElements.length} />);
        }
        this.renderDropdownMenuItems(item, menuItemElements);
        return;
      }
      const adminDashboardLink =
        this.props.user &&
        item.target_url === `${this.props.adminifiDomain}/${this.props.organizationSlug}/cportal/reports`;
      const href = adminDashboardLink ? 'confirmation_modal' : item.target_url;
      const buttonClick = this.buttonOrLink(item, href, true);
      const menuTitle = menuItem.slug || menuItem.css_class;
      const dataAttribute = item.data_attribute || null;
      if (this.showMenuItem(item)) {
        menuItemElements.push(
          <Dropdown.Item
            key={menuItemElements.length}
            id={`headerMenu-${menuTitle}-headerMenuItem-${subKey}`}
            data-attribute={dataAttribute}
            className={item.sub_text ? 'has-sub-text' : ''}
            {...buttonClick}
          >
            {item.icon && <FAIcon icon={item.icon} />}
            {item.title}
            {item.sub_text && <span className={`menu-sub-text ${item.css_class}`}>{item.sub_text}</span>}
          </Dropdown.Item>
        );
      }
    });

    return menuItemElements;
  };

  renderLink(menuItem, key) {
    const buttonClick = this.buttonOrLink(menuItem, menuItem.target_url);
    return (
      <Button
        id={`headerMenu-${key}`}
        rounded="full"
        variant="solid"
        colorScheme="neutral"
        size="compact"
        label={menuItem.title}
        title={menuItem.title}
        aria-label={menuItem.title}
        tabIndex={0}
        data-action={menuItem.title.toLowerCase()}
        icon={menuItem.icon}
        {...buttonClick}
      />
    );
  }

  renderChildren() {
    const menuData = this.props.menuData;

    return menuData.submenu_items.map((item, key) => {
      return (
        <li key={key}>
          {item.submenu_items && item.submenu_items.length
            ? this.renderDropdown(item, key)
            : this.renderLink(item, key)}
        </li>
      );
    });
  }

  setOpenDropdownClass = () => {
    if (typeof window === 'undefined') {
      return;
    }
    const openDropdownClass = 'header-menu-dropdown-open';
    const body = document.querySelector('body');
    const anyDropdownsOpen =
      Object.keys(this.state.dropdowns).length && Object.values(this.state.dropdowns).some(val => !!val);
    const bodyHasOpenClass = body.classList.contains(openDropdownClass);
    if (anyDropdownsOpen && !bodyHasOpenClass) {
      body.classList.add(openDropdownClass);
    } else if (!anyDropdownsOpen && bodyHasOpenClass) {
      body.classList.remove(openDropdownClass);
    }
  };

  render() {
    const { additionalNavClass, hideNameOnMobile, pullRight } = this.props;
    this.setOpenDropdownClass();
    const classNames = formatClassName([
      'header-menu',
      { 'mobile-hide-name': hideNameOnMobile },
      { 'pull-right': pullRight },
      additionalNavClass,
    ]);
    return (
      <nav className={classNames} aria-label={i18n.t('Header Menu')}>
        <Layout as={'ul'} variant={'flex'}>
          {this.renderChildren()}
        </Layout>
        {this.renderLanguageModal()}
        {this.renderSchoolModal()}
        {this.renderHelpModal()}
        {this.renderConfirmationModal()}
      </nav>
    );
  }
}

HeaderMenu.propTypes = propTypes;

HeaderMenu.defaultProps = defaultProps;
